import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import AppShell from "../components/layout/AppShell";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Icon from "../components/ui/Icon";
import { Parse } from "../lib/parse";
import { MAX_DOCUMENT_FILE_SIZE_MB } from "../config/uploadLimits";

const DOCUMENT_CLASS = "contracts_Document";
const EXTENDED_USER_CLASS = "contracts_Users";
const DEFAULT_MIN_SIGNATURE_AREA = 1500;
const DEFAULT_MIN_SIGNATURE_LENGTH = 180;
const SUPPORTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg"];

const parseStoredJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const readFileAsArrayBuffer = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event?.target?.result || null);
    reader.onerror = () => reject(reader.error || new Error("File read error"));
    reader.readAsArrayBuffer(file);
  });

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(String(event?.target?.result || ""));
    reader.onerror = () => reject(reader.error || new Error("File read error"));
    reader.readAsDataURL(file);
  });

const buildPdfFileName = (fileName = "") => {
  const cleanedName = String(fileName || "")
    .trim()
    .replace(/\.[^/.]+$/, "")
    .trim();
  const safeName = (cleanedName || "document")
    .normalize("NFKD")
    .replace(/[^\w.-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  const normalizedSafeName = safeName || `document_${Date.now()}`;
  return `${normalizedSafeName}.pdf`;
};

const convertImageToPdfBytes = async (file) => {
  const { PDFDocument } = await import("pdf-lib");
  const dataUrl = await readFileAsDataUrl(file);
  const pdfDoc = await PDFDocument.create();
  const imageType = String(file?.type || "").toLowerCase();
  const image =
    imageType === "image/png" ? await pdfDoc.embedPng(dataUrl) : await pdfDoc.embedJpg(dataUrl);
  const page = pdfDoc.addPage([image.width, image.height]);
  page.drawImage(image, {
    x: 0,
    y: 0,
    width: image.width,
    height: image.height
  });
  return pdfDoc.save({ useObjectStreams: false });
};

const generateTitleFromFilename = (filename) => {
  try {
    let title = String(filename || "").trim();
    const lastDotIndex = title.lastIndexOf(".");
    if (lastDotIndex > 0) {
      title = title.slice(0, lastDotIndex);
    }
    title = title.replace(/[^\p{L}\p{N}\s-]/gu, " ");
    title = title.replace(/\s+/g, " ");
    title = title.replace(/\p{L}\S*/gu, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());
    if (title.length > 100) {
      title = title.slice(0, 100).trim();
    }
    return title || "Untitled Document";
  } catch (error) {
    return "Untitled Document";
  }
};

function SignYourselfPage() {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const extUser = useMemo(() => {
    const extClass = parseStoredJson("Extand_Class");
    return Array.isArray(extClass) ? extClass?.[0] || {} : {};
  }, []);
  const navigate = useNavigate();

  const [fileName, setFileName] = useState("");
  const [uploadedFileUrl, setUploadedFileUrl] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [folders, setFolders] = useState([]);
  const [formState, setFormState] = useState({
    title: "",
    note: "",
    folderId: ""
  });

  const defaultSelfNote = t("new-ui.sign-yourself.default-note", {
    defaultValue: "Please sign this document."
  });

  useEffect(() => {
    setFormState((current) => ({
      ...current,
      note: current.note || defaultSelfNote
    }));
  }, [defaultSelfNote]);

  const loadWorkspaceData = async () => {
    try {
      const currentUser = Parse.User.current();
      if (!currentUser) {
        throw new Error(t("invalid-session", { defaultValue: "Invalid session." }));
      }

      const folderQuery = new Parse.Query(DOCUMENT_CLASS);
      folderQuery.equalTo("Type", "Folder");
      folderQuery.equalTo("CreatedBy", currentUser);
      folderQuery.notEqualTo("IsArchive", true);
      folderQuery.ascending("Name");
      folderQuery.limit(200);

      const folderResults = await folderQuery.find();
      const normalizedFolders = folderResults.map((folder) => ({
        objectId: folder.id,
        Name: folder.get("Name") || ""
      }));

      setFolders(normalizedFolders);
    } catch (loadError) {
      console.error("Sign yourself data load failed", loadError);
      setError(
        loadError?.message ||
          t("new-ui.dashboard.errors.load-failed", {
            defaultValue: "Could not load the dashboard from backend."
          })
      );
    }
  };

  useEffect(() => {
    loadWorkspaceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const triggerFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setError("");
    setUploadProgress(0);
    setUploadedFileUrl("");
    setFileName("");

    const fileSizeMb = file.size / (1024 * 1024);
    if (fileSizeMb > MAX_DOCUMENT_FILE_SIZE_MB) {
      setError(
        `${t("file-alert-1", { defaultValue: "Selected file is too large." })} ${MAX_DOCUMENT_FILE_SIZE_MB} MB.`
      );
      event.target.value = "";
      return;
    }
    const normalizedType = String(file.type || "").toLowerCase();
    const isPdf = normalizedType === "application/pdf" || String(file.name || "").toLowerCase().endsWith(".pdf");
    const isSupportedImage = SUPPORTED_IMAGE_TYPES.includes(normalizedType);
    if (!isPdf && !isSupportedImage) {
      setError(
        t("new-ui.sign-yourself.only-supported-files", {
          defaultValue: "Only PDF, PNG, JPG and JPEG files are supported in this flow."
        })
      );
      event.target.value = "";
      return;
    }

    try {
      setIsUploading(true);
      const uploadName = buildPdfFileName(file.name);
      const uploadBytes = isPdf
        ? new Uint8Array(await readFileAsArrayBuffer(file))
        : new Uint8Array(await convertImageToPdfBytes(file));
      const parseFile = new Parse.File(uploadName, Array.from(uploadBytes), "application/pdf");
      const fileResult = await parseFile.save({
        progress: (progressValue, loaded, total, meta = {}) => {
          if (meta.type === "upload" && total > 0) {
            setUploadProgress(Math.max(0, Math.min(100, Math.round((loaded * 100) / total))));
            return;
          }
          if (typeof progressValue === "number") {
            setUploadProgress(Math.max(0, Math.min(100, Math.round(progressValue * 100))));
          }
        }
      });

      const sourceUrl = fileResult?.url?.() || parseFile.url();
      if (!sourceUrl) {
        throw new Error(
          t("new-ui.sign-yourself.upload-failed", {
            defaultValue: "Could not upload file."
          })
        );
      }

      // Keep the canonical file URL in DB; it can be re-signed later when opening the editor.
      setUploadedFileUrl(sourceUrl);
      setFileName(isSupportedImage ? `${file.name} (PDF)` : file.name);
      setFormState((current) => ({
        ...current,
        title: current.title || generateTitleFromFilename(file.name)
      }));
    } catch (uploadError) {
      console.error("Sign yourself upload failed", uploadError);
      setError(
        uploadError?.message ||
          t("new-ui.sign-yourself.upload-failed", {
            defaultValue: "Could not upload file."
          })
      );
      if (event.target) {
        event.target.value = "";
      }
    } finally {
      setIsUploading(false);
    }
  };

  const clearForm = () => {
    setFileName("");
    setUploadedFileUrl("");
    setUploadProgress(0);
    setError("");
    setFormState({
      title: "",
      note: defaultSelfNote,
      folderId: ""
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const openSigningEditor = (documentId) => {
    if (!documentId) {
      return;
    }
    navigate(`/signaturePdf/${documentId}`);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!uploadedFileUrl) {
      setError(t("file-alert-3", { defaultValue: "Please wait while the document uploads." }));
      return;
    }

    const trimmedTitle = String(formState.title || "").trim();
    if (!trimmedTitle) {
      setError(
        t("new-ui.sign-yourself.title-required", {
          defaultValue: "Document title is required."
        })
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const currentUser = Parse.User.current();
      if (!currentUser?.id) {
        throw new Error(t("invalid-session", { defaultValue: "Invalid session." }));
      }

      const documentObject = new Parse.Object(DOCUMENT_CLASS);
      documentObject.set("Name", trimmedTitle);
      documentObject.set("Note", String(formState.note || "").trim() || defaultSelfNote);
      documentObject.set("URL", uploadedFileUrl);
      documentObject.set("CreatedBy", Parse.User.createWithoutData(currentUser.id));
      const tenantMinSignatureArea = Number.parseInt(extUser?.TenantId?.MinSignatureArea, 10);
      const tenantMinSignatureLength = Number.parseInt(extUser?.TenantId?.MinSignatureLength, 10);
      documentObject.set(
        "MinSignatureArea",
        tenantMinSignatureArea || Number.parseInt(extUser?.MinSignatureArea, 10) || DEFAULT_MIN_SIGNATURE_AREA
      );
      documentObject.set(
        "MinSignatureLength",
        tenantMinSignatureLength ||
          Number.parseInt(extUser?.MinSignatureLength, 10) ||
          DEFAULT_MIN_SIGNATURE_LENGTH
      );

      if (formState.folderId) {
        documentObject.set("Folder", {
          __type: "Pointer",
          className: DOCUMENT_CLASS,
          objectId: formState.folderId
        });
      }

      if (extUser?.objectId) {
        documentObject.set("ExtUserPtr", {
          __type: "Pointer",
          className: EXTENDED_USER_CLASS,
          objectId: extUser.objectId
        });
      }

      if (extUser?.TenantId?.ActiveFileAdapter) {
        documentObject.set("FileAdapterId", extUser.TenantId.ActiveFileAdapter);
      }

      const savedDocument = await documentObject.save();
      openSigningEditor(savedDocument?.id);
    } catch (saveError) {
      console.error("Failed to create self-sign document", saveError);
      setError(
        saveError?.message ||
          t("something-went-wrong-mssg", {
            defaultValue: "Something went wrong."
          })
      );
      setIsSubmitting(false);
    }
  };

  return (
    <AppShell>
      <div className="dana-section-grid">
        <Card className="animate-fade-up">
          <div className="dana-section-head">
            <span className="dana-section-icon">
              <Icon name="signature" />
            </span>
            <div>
              <p className="ui-kicker">
                {t("new-ui.sign-yourself.kicker", { defaultValue: "Cargar y firmar" })}
              </p>
              <h1 className="ui-title dana-section-title">
                {t("new-ui.sign-yourself.page-title", { defaultValue: "Firmar un documento yo mismo" })}
              </h1>
            </div>
          </div>
          <p className="ui-copy mt-3">{t("signyour-self-description", { defaultValue: "" })}</p>
        </Card>

        <Card className="animate-fade-up [animation-delay:40ms]">
          <form className="grid grid-cols-1 gap-3" onSubmit={handleSubmit}>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
                {`${t("report-heading.File", { defaultValue: "File" })} (${t("file-type", {
                  defaultValue: "pdf, png, jpg, jpeg"
                })})`}
              </label>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileChange}
                accept="application/pdf,image/png,image/jpeg,image/jpg"
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  onClick={triggerFilePicker}
                  disabled={isUploading || isSubmitting}
                  className="!border !border-[#1ea34f] !bg-[#22c55e] !text-white hover:!bg-[#1ea34f] focus-visible:!ring-[rgba(34,197,94,0.35)]"
                >
                  <span className="inline-flex items-center gap-2">
                    <Icon name="templateDoc" />
                    {t("new-ui.sign-yourself.choose-file", { defaultValue: "Cargar archivo" })}
                  </span>
                </Button>
                {fileName ? (
                  <p className="m-0 truncate text-xs text-app-ink-muted" title={fileName}>
                    {t("file-selected", { defaultValue: "Selected file" })}: {fileName}
                  </p>
                ) : (
                  <p className="m-0 text-xs text-app-ink-muted">
                    {t("new-ui.sign-yourself.no-file", { defaultValue: "No file selected." })}
                  </p>
                )}
              </div>
              <p className="m-0 mt-1 text-[11px] text-app-ink-muted">
                {t("new-ui.upload.max-size", {
                  defaultValue: "Maximum file size: {{size}} MB",
                  size: MAX_DOCUMENT_FILE_SIZE_MB
                })}
              </p>
              {isUploading ? (
                <div className="mt-2">
                  <div className="h-1.5 w-full rounded-full bg-slate-200">
                    <div className="h-1.5 rounded-full bg-app-brand transition-all" style={{ width: `${uploadProgress}%` }} />
                  </div>
                  <p className="m-0 mt-1 text-[11px] text-app-ink-muted">{uploadProgress}%</p>
                </div>
              ) : null}
            </div>

            <Input
              id="sign-yourself-title"
              label={t("document-title", { defaultValue: "Document title" })}
              value={formState.title}
              onChange={(event) => setFormState((current) => ({ ...current, title: event.target.value }))}
              required
            />

            <div className="w-full">
              <label
                htmlFor="sign-yourself-note"
                className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted"
              >
                {t("report-heading.Note", { defaultValue: "Note" })}
              </label>
              <textarea
                id="sign-yourself-note"
                className="min-h-[86px] w-full rounded-xl border border-app-line bg-white px-3 py-2.5 text-sm text-app-ink shadow-soft outline-none transition focus:border-app-brand focus:ring-4 focus:ring-app-brand/15"
                value={formState.note}
                onChange={(event) => setFormState((current) => ({ ...current, note: event.target.value }))}
              />
            </div>

            <div className="w-full">
              <label
                htmlFor="sign-yourself-folder"
                className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted"
              >
                {t("select-folder", { defaultValue: "Select folder" })}
              </label>
              <select
                id="sign-yourself-folder"
                className="w-full rounded-xl border border-app-line bg-white px-3 py-2.5 text-sm text-app-ink shadow-soft outline-none transition focus:border-app-brand focus:ring-4 focus:ring-app-brand/15"
                value={formState.folderId}
                onChange={(event) => setFormState((current) => ({ ...current, folderId: event.target.value }))}
              >
                <option value="">{t("new-ui.sign-yourself.no-folder", { defaultValue: "No folder" })}</option>
                {folders.map((folder) => (
                  <option key={folder.objectId} value={folder.objectId}>
                    {folder.Name || folder.objectId}
                  </option>
                ))}
              </select>
            </div>

            {error ? <p className="m-0 text-sm text-app-danger">{error}</p> : null}

            <div className="mt-1 flex flex-wrap gap-2">
              <Button type="submit" disabled={isUploading || isSubmitting || !uploadedFileUrl}>
                {isSubmitting
                  ? t("loading", { defaultValue: "Loading..." })
                  : t("new-ui.dashboard.quick-actions.sign-yourself", { defaultValue: "Sign yourself" })}
              </Button>
              <Button type="button" variant="ghost" onClick={clearForm} disabled={isSubmitting}>
                {t("reset", { defaultValue: "Reset" })}
              </Button>
              <Link to="/dashboard/35KBoSgoAK">
                <Button type="button" variant="ghost">
                  {t("new-ui.sections.back-dashboard", { defaultValue: "Back to dashboard" })}
                </Button>
              </Link>
            </div>
          </form>
        </Card>

      </div>
    </AppShell>
  );
}

export default SignYourselfPage;
